import { BlockStack, LegacyStack, Text } from '@shopify/polaris'
import { useContext } from 'react'
import useSWR from 'swr'

import { backendFetchResultData } from '../common/api'
import { anyWindow } from '../common/helpers'
import { averageLengthStatsEmptyData } from '../common/new-user-test-data'
import { SettingsContext } from '../settings-page/api'
import { BasicStatCard } from './BasicStats'

interface AverageLengthResult {
  avg: number | null
  count: number
}

export interface AverageLengthStatsResponse {
  averageLength: AverageLengthResult
  averageLengthForChurn: AverageLengthResult
}

const fetchAverageLengthStats = (_: any, query: string) => {
  if (anyWindow.newUserTest) {
    return Promise.resolve(averageLengthStatsEmptyData)
  }

  return backendFetchResultData<AverageLengthStatsResponse>(
    'GET',
    `/dashboard/average-length-stats?${query}`
  )
}

export const AverageLengthReport = ({
  filterQuery,
  filterLabel,
}: {
  filterQuery: string
  filterLabel: string
}) => {
  const userSettings = useContext(SettingsContext)

  const { data: averageLengthReportData } = useSWR<AverageLengthStatsResponse>(
    ['average-length-stats', filterQuery, userSettings?.analytics_start_date],
    fetchAverageLengthStats,
    {
      revalidateOnFocus: false,
    }
  )

  const loading = !averageLengthReportData

  return (
    <LegacyStack distribution="fillEvenly">
      <BasicStatCard
        title="Average subscription length"
        filterLabel={filterLabel}
        tooltip="The average duration of all subscriptions. This metric includes the period from activation (creation) to the next planned payment date for active and paused subscriptions. For completed subscriptions (cancelled, expired, failed), it includes the period from activation to the end of the last billing cycle."
        loading={loading}
      >
        {averageLengthReportData && (
          <>
            {averageLengthReportData.averageLength.avg && (
              <BlockStack gap="100">
                <Text variant="headingLg" as="p">
                  {averageLengthReportData.averageLength.avg} months
                </Text>
                <Text as="p" tone="subdued" variant="bodyMd">
                  Based on <Text as="strong">{averageLengthReportData.averageLength.count}</Text>{' '}
                  subscriptions.
                </Text>
              </BlockStack>
            )}
            {!averageLengthReportData.averageLength.avg && (
              <Text tone="subdued" as="p">
                &ndash;
              </Text>
            )}
          </>
        )}
      </BasicStatCard>
      <BasicStatCard
        title="Average completed subscription length"
        filterLabel={filterLabel}
        tooltip="The average duration of subscriptions that have ended, including cancelled, expired, and failed subscriptions. This metric calculates the period from activation to the end of the last billing cycle, not necessarily the date when the subscription was cancelled or expired."
        loading={loading}
      >
        {averageLengthReportData && (
          <>
            {averageLengthReportData.averageLengthForChurn.avg && (
              <BlockStack gap="100">
                <Text variant="headingLg" as="p">
                  {averageLengthReportData.averageLengthForChurn.avg} months
                </Text>
                <Text as="p" tone="subdued" variant="bodyMd">
                  Based on{' '}
                  <Text as="strong">{averageLengthReportData.averageLengthForChurn.count}</Text>{' '}
                  subscriptions.
                </Text>
              </BlockStack>
            )}
            {!averageLengthReportData.averageLengthForChurn.avg && (
              <Text tone="subdued" as="p">
                &ndash;
              </Text>
            )}
          </>
        )}
      </BasicStatCard>
    </LegacyStack>
  )
}
